import { currentSentence, TEAM_COLOURS, type PlayerInfo, type PlayerProgress, type TypingState } from '@typing-racer/shared';
import { clear, h, ordinal, pct } from '../dom.js';
import { KeyboardView } from './keyboard.js';

export interface HudModel {
  myId: string | null;
  players: PlayerInfo[];
  progress: Map<string, PlayerProgress>;
  typing: TypingState | null;
  rollingWpm: number;
  accuracy: number;
  stalled: boolean;
  finishedPlace: number | null;
}

export interface HudOptions {
  mode: 'race' | 'lesson';
  title?: string;
  viewName: string;
  musicName: string;
  sfxOn: boolean;
  typingName: string;
  onView(): void;
  onMusic(): void;
  onSfx(): void;
  onTyping(): void;
  onQuit(): void;
}

export class Hud {
  private root: HTMLElement;
  private wpmEl = h('div', { class: 'v' }, '0');
  private accEl = h('div', { class: 'v' }, '100%');
  private posEl = h('div', { class: 'v' }, '-');
  private sentEl = h('div', { class: 'v' }, '1/5');
  private mini = h('div', { class: 'minimap' }, h('div', { class: 'lane' }), h('span', { class: 'flag' }, '🏁'));
  private dots = new Map<string, HTMLElement>();
  private sentence = h('div', { class: 'sentence', 'aria-live': 'off' });
  private next = h('div', { class: 'next' });
  private meaningEl = h('div', { class: 'meaning', hidden: true });
  /** Meaning per line (language lessons). */
  meanings: string[] | null = null;
  private panel: HTMLElement;
  private viewBtn: HTMLElement;
  private musicBtn: HTMLElement;
  private sfxBtn: HTMLElement;
  private typingBtn: HTMLElement;
  readonly keyboard: KeyboardView | null;
  private lastSentenceIndex = -1;
  private lastCursor = -1;
  private lastErr = '';

  constructor(parent: HTMLElement, private opts: HudOptions) {
    const lesson = opts.mode === 'lesson';
    this.keyboard = lesson ? new KeyboardView() : null;
    this.panel = h('div', { class: 'sentence-panel' }, this.sentence, this.meaningEl, this.next, this.keyboard?.el ?? null);
    this.viewBtn = h('button', { class: 'btn small ghost', title: 'Change camera (Tab)', onclick: () => opts.onView() }, `🎥 ${opts.viewName}`);
    this.musicBtn = h('button', { class: 'btn small ghost', title: 'Music on/off', onclick: () => opts.onMusic() }, `🎵 ${opts.musicName}`);
    this.sfxBtn = h('button', { class: 'btn small ghost', title: 'Sound effects on/off', onclick: () => opts.onSfx() }, opts.sfxOn ? '🔊 On' : '🔇 Off');
    this.typingBtn = h('button', { class: 'btn small ghost', title: 'Typing sounds', onclick: () => opts.onTyping() }, `⌨️ ${opts.typingName}`);
    this.root = h(
      'div',
      {},
      h(
        'div',
        { class: 'hud-top' },
        lesson ? h('div', { class: 'stat title' }, h('div', { class: 'v' }, opts.title ?? 'Lesson'), h('div', { class: 'l' }, 'Lesson')) : h('div', { class: 'stat' }, this.posEl, h('div', { class: 'l' }, 'Position')),
        lesson ? h('div', { style: 'flex:1' }) : this.mini,
        h('div', { class: 'stat' }, this.wpmEl, h('div', { class: 'l' }, 'WPM')),
        h('div', { class: 'stat' }, this.accEl, h('div', { class: 'l' }, 'Accuracy')),
        h('div', { class: 'stat' }, this.sentEl, h('div', { class: 'l' }, lesson ? 'Line' : 'Sentence')),
      ),
      h(
        'div',
        { class: 'hud-tools' },
        this.viewBtn,
        this.musicBtn,
        this.sfxBtn,
        this.typingBtn,
        h('button', { class: 'btn small red', title: 'Quit (Esc)', onclick: () => opts.onQuit() }, '✕ Quit'),
      ),
      h('div', { class: 'hud-bottom' }, this.panel),
    );
    parent.append(this.root);
  }

  setViewName(n: string): void {
    this.viewBtn.textContent = `🎥 ${n}`;
  }
  setMusicName(n: string): void {
    this.musicBtn.textContent = `🎵 ${n}`;
  }
  setSfx(on: boolean): void {
    this.sfxBtn.textContent = on ? '🔊 On' : '🔇 Off';
  }
  setTypingName(n: string): void {
    this.typingBtn.textContent = `⌨️ ${n}`;
  }

  destroy(): void {
    this.keyboard?.destroy();
    this.root.remove();
  }

  shake(): void {
    this.panel.classList.remove('shake');
    void this.panel.offsetWidth;
    this.panel.classList.add('shake');
  }

  flashNitro(): void {
    const f = h('div', { class: 'nitro-flash' });
    this.root.append(f);
    setTimeout(() => f.remove(), 600);
  }

  update(m: HudModel): void {
    this.wpmEl.textContent = String(Math.round(m.rollingWpm));
    this.accEl.textContent = pct(m.accuracy);
    const t = m.typing;
    if (t) {
      this.sentEl.textContent = `${Math.min(t.sentenceIndex + 1, t.sentences.length)}/${t.sentences.length}`;
      if (t.sentenceIndex !== this.lastSentenceIndex || t.cursor !== this.lastCursor || t.errorBuffer !== this.lastErr) {
        this.renderSentence(t);
      }
    }
    this.panel.classList.toggle('stalled', m.stalled);
    if (this.opts.mode === 'lesson') return;

    // Position among all players by progress (finished players keep their place).
    const list = [...m.progress.values()];
    const mine = m.myId ? m.progress.get(m.myId) : undefined;
    if (m.finishedPlace) this.posEl.textContent = ordinal(m.finishedPlace);
    else if (mine) {
      const ahead = list.filter((p) => p.id !== mine.id && (p.finished || p.progress > mine.progress)).length;
      this.posEl.textContent = `${ordinal(ahead + 1)}`;
    }

    // Minimap dots
    for (const p of m.players) {
      let d = this.dots.get(p.id);
      const colour = p.team !== null ? (TEAM_COLOURS[p.team] ?? p.colour) : p.colour;
      if (!d) {
        d = h('div', { class: `dot${p.id === m.myId ? ' me' : ''}${p.team !== null ? ' team' : ''}`, title: p.name }, p.name.slice(0, 2).toUpperCase());
        this.dots.set(p.id, d);
        this.mini.append(d);
      }
      if (d.style.background !== colour) d.style.background = colour;
      const pr = m.progress.get(p.id)?.progress ?? 0;
      d.style.left = `calc(10px + (100% - 40px) * ${pr.toFixed(4)})`;
    }
    for (const [id, d] of this.dots) {
      if (!m.players.some((p) => p.id === id)) {
        d.remove();
        this.dots.delete(id);
      }
    }
  }

  private renderSentence(t: TypingState): void {
    this.lastSentenceIndex = t.sentenceIndex;
    this.lastCursor = t.cursor;
    this.lastErr = t.errorBuffer;
    clear(this.sentence);
    const s = currentSentence(t);
    if (!s) {
      this.sentence.append(h('span', { class: 'done' }, 'Finished! 🏁'));
      this.next.textContent = '';
      this.keyboard?.setNext(null);
      return;
    }
    const done = s.slice(0, t.cursor);
    const errLen = t.errorBuffer.length;
    const err = s.slice(t.cursor, t.cursor + errLen).padEnd(errLen, ' ');
    const cur = s[t.cursor + errLen] ?? '';
    const rest = s.slice(t.cursor + errLen + 1);
    this.sentence.append(h('span', { class: 'done' }, done));
    if (errLen) this.sentence.append(h('span', { class: 'err' }, err));
    this.sentence.append(h('span', { class: 'cur' }, cur || ' '), h('span', { class: 'rest' }, rest));
    const nxt = t.sentences[t.sentenceIndex + 1];
    clear(this.next);
    if (nxt) {
      this.next.append(h('b', {}, 'Next: '), nxt);
      const nm = this.meanings?.[t.sentenceIndex + 1];
      if (nm) this.next.append(h('span', { class: 'muted' }, `  (${nm})`));
    } else this.next.append(h('b', {}, this.opts.mode === 'lesson' ? 'Last line!' : 'Last sentence!'));
    const meaning = this.meanings?.[t.sentenceIndex];
    this.meaningEl.hidden = !meaning;
    if (meaning) {
      clear(this.meaningEl);
      this.meaningEl.append(h('b', {}, 'Meaning: '), meaning);
    }
    this.keyboard?.setNext(errLen ? null : (s[t.cursor] ?? null), errLen > 0);
  }
}

export function renderCountdown(parent: HTMLElement, n: number | string, tip: string): HTMLElement {
  const el = h(
    'div',
    { class: 'countdown' },
    h('div', { class: 'num', key: String(n) }, String(n)),
    h('div', { class: 'tip panel' }, h('b', {}, '💡 Tip: '), tip),
  );
  parent.append(el);
  return el;
}
